<template>
	<div class="youtube-container">
		<div class="aspect-ratio-box">
			<div
				class="aspect-ratio-box-inside error"
				v-if="errored"
			>
				<div>
					<h3>There was an error loading your video.</h3>
					<p>Please make sure you're connected to the internet.</p>
					<p>
						If the issue persists it may be a firewall issue, please try
						<a
							:href="url"
							target="_blank"
							>this link</a
						>.
					</p>
					<p>
						Finally, if the issue still persists, please email support on
						<a
							href="mailto:tech@realschools.com.au"
							target="_blank"
							>tech@realschools.com.au</a
						>
					</p>
				</div>
			</div>
			<div
				class="aspect-ratio-box-inside"
				v-else
			>
				<button
					class="ended"
					v-show="ended"
					@click="replay"
				>
					<img src="@/images/youtube-replay.svg?inline" />
				</button>
				<div
					class="player-container"
					v-show="shouldShowIFrame"
				>
					<div :id="`player-${id}`"></div>
				</div>
				<button
					class="play-button"
					@click="requestPlaying"
					v-show="shouldShowPlayButton"
				>
					<img src="@/images/youtube-play.svg?inline" />
				</button>

				<LoadingComponent
					:loading="shouldShowLoading"
					:outer-border="false"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { wrapBackgroundImageUrlToCss } from '@/helpers';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';
import { defineComponent } from 'vue';
import { loadYoutube } from '@/services/scriptLoaderService';

enum PlayerState {
	UNSTARTED = -1,
	ENDED = 0,
	PLAYING = 1,
	PAUSED = 2,
	BUFFERING = 3,
	CUED = 5,
}

export default defineComponent({
	components: { LoadingComponent },
	props: {
		id: {
			type: String,
			required: true,
		},
		preload: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			playerCache: null as Promise<YT.Player> | null,
			userHasRequestedPlaying: false,
			hasPlayedFirstTime: false,
			playerState: PlayerState.UNSTARTED,
			errored: false,
			overrideDisableLoading: false,
		};
	},
	computed: {
		thumbnail() {
			return wrapBackgroundImageUrlToCss(`https://img.youtube.com/vi/${this.id}/maxresdefault.jpg`);
		},
		playing() {
			return this.playerState === PlayerState.PLAYING;
		},
		ended() {
			return this.playerState === PlayerState.ENDED;
		},
		shouldShowPlayButton() {
			return !this.ended && !this.hasPlayedFirstTime && !this.shouldShowLoading;
		},
		shouldShowLoading() {
			return this.userHasRequestedPlaying && !this.hasPlayedFirstTime && !this.overrideDisableLoading;
		},
		shouldShowIFrame() {
			return !this.ended && this.hasPlayedFirstTime;
		},
		url() {
			return `https://www.youtube.com/watch?v=${this.id}`;
		},
	},
	watch: {
		async id() {
			this.reset();
			this.init();
		},
	},
	async beforeMount() {
		this.init();
	},
	async unmounted() {
		this.reset();
	},
	methods: {
		async init() {
			if (this.preload) {
				await this.getPlayer();
			}
		},
		async reset() {
			const player = await this.getPlayer();
			player?.destroy();

			this.playerCache = null;
			this.userHasRequestedPlaying = false;
			this.hasPlayedFirstTime = false;
			this.playerState = PlayerState.UNSTARTED;
			this.errored = false;
			this.overrideDisableLoading = false;
		},
		async getPlayer() {
			if (this.playerCache) {
				return this.playerCache;
			}

			try {
				const Player = await loadYoutube();

				this.playerCache = new Promise((resolve) => {
					const player = new Player(`player-${this.id}`, {
						videoId: this.id,
						playerVars: {
							controls: 1,
							rel: 0,
							showinfo: 0,
							modestbranding: 1,
							iv_load_policy: 3,
							playsinline: 1,
						},
						events: {
							onReady: () => {
								if (this.userHasRequestedPlaying) {
									player.playVideo();
								}

								resolve(player);
							},
							onStateChange: this.onPlayerStateChange,
						},
					});
				});

				return this.playerCache;
			} catch (error) {
				this.errored = true;
				console.error(`Error loading YouTube player: ${error}`);
			}
		},
		onPlayerStateChange(event: YT.OnStateChangeEvent) {
			if (event.data === PlayerState.PLAYING) {
				this.hasPlayedFirstTime = true;
			}

			// Some mobile devices won't allow us to call play programmatically without a direct user interaction
			// (which incudes some awaits for script and iframe loading) so when we request playing it seems to go
			// from buffering to paused without ever going to playing. Therefore we also check for PAUSED here and
			// we'll assume that if it is paused after a play request, it must have played at least once/wants to play.
			if (event.data === PlayerState.PAUSED) {
				this.hasPlayedFirstTime = true;
			}

			this.playerState = event.data;
		},
		async requestPlaying() {
			this.userHasRequestedPlaying = true;

			(await this.getPlayer())?.playVideo();

			setTimeout(() => {
				this.overrideDisableLoading = true;
			}, 1000);
		},
		async replay() {
			const player = await this.getPlayer();
			if (!player) {
				return;
			}

			player.seekTo(0, true);
			player.playVideo();
		},
	},
});
</script>

<style scoped lang="scss">
.youtube-container {
	.aspect-ratio-box {
		padding-top: calc(9 / 16 * 100%);
	}

	.aspect-ratio-box-inside {
		display: flex;
		justify-content: center;
		align-items: center;
		background-image: v-bind(thumbnail);
		background-position: center;
		background-size: cover;
		background-repeat: no-repeat;

		> * {
			flex: 100%;
		}

		.player-container {
			width: 100%;
			height: 100%;

			:deep() {
				iframe {
					width: 100%;
					height: 100%;
				}
			}
		}

		.play-button {
			transition: opacity 0.15s;

			&:hover {
				opacity: 0.8;
			}
		}

		.ended {
			transition: opacity 0.15s;

			&:hover {
				opacity: 0.8;
			}
		}

		&.error {
			background-image: none;
			background-color: #f8d7da;
			padding: 1rem;
			color: #721c24;
			text-align: center;

			h3 {
				margin-bottom: 20px;
			}

			a {
				color: #721c24;
				text-decoration: underline;
			}
		}
	}
}
</style>
